import { useState } from 'react'

import { Button, SegmentedControl, type SegmentedOption } from '@/components/ui'
import { PLAN_KINDS, useAccountMutating, useCheckout } from '@/modules/account'
import {
  formatCredits,
  formatOneTimePrice,
  isSingleProject,
  type Plan,
  singleProjectPlan,
  usePlans,
} from '@/modules/auth'
import { cn } from '@/utils'
import { currencyFromCents } from '@/utils/format'
import PlanFeatures from './PlanInformation/PlanFeatures'

type BillingInterval = 'month' | 'year'

const INTERVAL_OPTIONS: SegmentedOption<BillingInterval>[] = [
  { value: 'month', label: 'Monthly' },
  { value: 'year', label: 'Yearly' },
]

const EXTRACTION = 'Extraction'
const AI_CHAT = 'AI Chat'
const FORENSIC_REPORTS = 'Forensic reports'
const RECONCILIATION = 'Self-healing reconciliation'
const ALL_FEATURES = [EXTRACTION, AI_CHAT, FORENSIC_REPORTS, RECONCILIATION]
const AI_FEATURE = 'ai_features'
const RECONCILIATION_FEATURE = 'statement_reconciliation'
const TIER_NAMES = ['Core', 'Plus', 'Premier']
const TRIAL_TIER = 'Premier'
// Core is being retired as a plan, so the trial no longer offers it.
const RETIRED_TIER = 'Core'
const SINGLE_PROJECT = 'Single project'
const CARD_ORDER = ['Plus', 'Premier']

const tierName = (plan: Plan) =>
  isSingleProject(plan)
    ? SINGLE_PROJECT
    : (TIER_NAMES.find((tier) => plan.displayName.includes(tier)) ??
      plan.displayName)

const describePlan = (plan: Plan) => {
  if (isSingleProject(plan)) {
    return 'One case, one flat fee.'
  }
  const billed = plan.interval === 'year' ? 'Billed yearly.' : 'Billed monthly.'
  return tierName(plan) === TRIAL_TIER
    ? `${billed} The tier your trial ran on.`
    : billed
}

const priceUnit = (plan: Plan) => {
  if (isSingleProject(plan)) return 'one time'
  return plan.interval === 'year' ? 'a year' : 'a month'
}

const includedPages = (plan: Plan) => {
  const pages = `${formatCredits(plan)} pages included`
  if (isSingleProject(plan)) return pages
  return plan.interval === 'year' ? `${pages} each year` : `${pages} each month`
}

const formatPrice = (plan: Plan) =>
  isSingleProject(plan)
    ? formatOneTimePrice(plan)
    : currencyFromCents(plan.unitAmount)

const hasChat = (plan: Plan) =>
  isSingleProject(plan) || plan.features.includes(AI_FEATURE)

const hasReconciliation = (plan: Plan) =>
  isSingleProject(plan) || plan.features.includes(RECONCILIATION_FEATURE)

const includedFeatures = (plan: Plan) => [
  EXTRACTION,
  ...(hasChat(plan) ? [AI_CHAT, FORENSIC_REPORTS] : []),
  ...(hasReconciliation(plan) ? [RECONCILIATION] : []),
]

const isOfferedSubscription = (plan: Plan, interval: BillingInterval) =>
  plan.kind !== PLAN_KINDS.trial &&
  !isSingleProject(plan) &&
  tierName(plan) !== RETIRED_TIER &&
  plan.interval === interval

const cardPosition = (plan: Plan) => {
  const position = CARD_ORDER.indexOf(tierName(plan))
  return position === -1 ? CARD_ORDER.length : position
}

const subscriptionsFor = (plans: Plan[], interval: BillingInterval) =>
  plans
    .filter((plan) => isOfferedSubscription(plan, interval))
    .sort((first, second) => cardPosition(first) - cardPosition(second))

interface PlanCardProps {
  plan: Plan
  isRedirecting: boolean
  onChoose: (plan: Plan) => void
  className?: string
}

const PlanCard = ({
  plan,
  isRedirecting,
  onChoose,
  className,
}: PlanCardProps) => {
  const headingId = `plan-${plan.priceId}`
  return (
    <section
      aria-labelledby={headingId}
      className={cn(
        'row-span-6 grid grid-rows-subgrid gap-4 rounded-lg border bg-white p-5',
        className,
      )}
    >
      <div className="flex flex-col gap-1">
        <h3 id={headingId} className="font-semibold">
          {tierName(plan)}
        </h3>
        <p className="text-muted-foreground text-[13px]">
          {describePlan(plan)}
        </p>
      </div>
      <p className="flex items-baseline gap-1.5">
        <span className="text-2xl font-bold tabular-nums">
          {formatPrice(plan)}
        </span>
        <span className="text-muted-foreground text-[13px]">
          {priceUnit(plan)}
        </span>
      </p>
      <div className="flex flex-col gap-0.5">
        <p className="text-sm font-medium tabular-nums">
          {includedPages(plan)}
        </p>
        <p className="text-muted-foreground text-[13px]">
          Need more? Buy extra pages any time.
        </p>
      </div>
      <PlanFeatures
        features={includedFeatures(plan)}
        available={ALL_FEATURES}
        className="flex flex-col gap-2.5"
      />
      <p className="text-[13px]">
        {isSingleProject(plan) && 'Your trial case becomes this project.'}
      </p>
      <Button
        className="w-full self-end"
        isLoading={isRedirecting}
        loadingText="Redirecting..."
        onClick={() => onChoose(plan)}
      >
        Continue to checkout
      </Button>
    </section>
  )
}

interface GroupHeadingProps {
  title: string
  detail: string
  className?: string
}

const GroupHeading = ({ title, detail, className }: GroupHeadingProps) => (
  <div className={cn('flex flex-col gap-0.5 self-end', className)}>
    <h3 className="text-base font-semibold">{title}</h3>
    <p className="text-muted-foreground text-[13px]">{detail}</p>
  </div>
)

export const PlanCards = () => {
  const plans = usePlans()
  const isMutating = useAccountMutating()
  const { buyProject, selectPlan } = useCheckout()
  const [billingInterval, setBillingInterval] =
    useState<BillingInterval>('month')
  const [chosenPriceId, setChosenPriceId] = useState<string | null>(null)

  const choose = (plan: Plan) => {
    setChosenPriceId(plan.priceId)
    if (isSingleProject(plan)) buyProject()
    else selectPlan(plan.priceId)
  }

  const singleProject = singleProjectPlan(plans)

  // On a phone the grid is one column, so `order` keeps each heading above its own cards.
  return (
    <div className="flex flex-col gap-6">
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_1px_minmax(0,1fr)_minmax(0,1fr)] lg:gap-x-6">
        <GroupHeading
          title="One case"
          detail="Pay once. The case stays open for a year."
          className="order-1 lg:col-start-1 lg:row-start-1"
        />
        <div
          aria-hidden
          className="bg-border hidden lg:col-start-2 lg:row-span-7 lg:row-start-1 lg:block"
        />
        <div className="order-3 mt-6 flex items-end justify-between gap-4 lg:col-span-2 lg:col-start-3 lg:row-start-1 lg:mt-0">
          <GroupHeading
            title="Subscriptions"
            detail="Renews every month or every year."
          />
          <SegmentedControl
            ariaLabel="Billing period"
            options={INTERVAL_OPTIONS}
            value={billingInterval}
            onChange={setBillingInterval}
          />
        </div>
        {singleProject && (
          <PlanCard
            plan={singleProject}
            isRedirecting={
              isMutating && chosenPriceId === singleProject.priceId
            }
            onChoose={choose}
            className="order-2 lg:col-start-1 lg:row-start-2"
          />
        )}
        {subscriptionsFor(plans, billingInterval).map((plan) => (
          <PlanCard
            key={plan.priceId}
            plan={plan}
            isRedirecting={isMutating && chosenPriceId === plan.priceId}
            onChoose={choose}
            className="order-4"
          />
        ))}
      </div>
      <p className="text-muted-foreground text-sm">
        Your trial case and everything in it stay. The rest of your file is
        extracted after upgrade.
      </p>
    </div>
  )
}
